/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Row, Col } from "react-awesome-styled-grid";
import { Link } from "react-router-dom";
import { Box, Text, FlexContainer, theme } from "@nordcloud/gnui";

import { useTeamLeaderDashboardQuery, Maybe } from "~/generated/graphql";
import { LoaderWrap } from "~/components";
import { SpendsChart } from "~/components/Charts/SpendsChart";
import { useCurrency } from "~/hooks";
import { ROUTES } from "~/routing/routes";
import { formatMoneyWithFallback } from "~/tools";
import { WhatsNewPopup } from "~/views/whatsnew/Whatsnew";

type EnvToResourceCount = {
  resourceCount: number;
};

const BoxToWindowWidthRatio = 2.2;
const MinBoxWidth = 650;

const sumApplicationResourceCounts = (
  n: number,
  e: Maybe<EnvToResourceCount>
) => n + (e?.resourceCount || 0);

export function HomePageTeamLeader() {
  const { currency } = useCurrency();

  const { data, loading } = useTeamLeaderDashboardQuery();

  const chartWidth = Math.min(
    window.innerWidth / BoxToWindowWidthRatio,
    MinBoxWidth
  );
  const applications = data?.applications ?? [];

  return (
    <LoaderWrap loading={loading}>
      <WhatsNewPopup />
      <Row>
        {applications.map((app) => (
          <Col key={app?.nid} lg="6">
            <Box mb="1rem">
              <Row css={{ marginBottom: theme.spacing.spacing01 }}>
                <Col>
                  <FlexContainer>
                    <div>
                      <Text
                        size="lg"
                        weight="medium"
                        tag="div"
                        mb={theme.spacing.spacing01}
                        ml={theme.spacing.spacing04}
                      >
                        <Link to={`${ROUTES.applications.index}/${app?.nid}`}>
                          {app?.name}
                        </Link>
                      </Text>
                      <FlexContainer>
                        <Text size="sm" tag="span" ml={theme.spacing.spacing04}>
                          {app?.environments?.length || 0} environments
                        </Text>
                        <Text size="sm" tag="span" ml={theme.spacing.spacing04}>
                          {app?.environments?.reduce(
                            sumApplicationResourceCounts,
                            0
                          )}{" "}
                          resources
                        </Text>
                      </FlexContainer>
                    </div>
                  </FlexContainer>
                </Col>
                <Col>
                  <FlexContainer justifyContent="flex-end">
                    <div css={{ paddingRight: theme.spacing.spacing04 }}>
                      <Text size="sm" tag="div" mb={theme.spacing.spacing01}>
                        Monthly Costs:
                      </Text>
                      <Text size="lg" weight="medium" tag="div">
                        {formatMoneyWithFallback(
                          app?.cost?.currentMonth,
                          currency
                        )}
                      </Text>
                    </div>
                    <div>
                      <Text size="sm" tag="div" mb={theme.spacing.spacing01}>
                        Monthly Forecast:
                      </Text>
                      <Text size="lg" weight="medium" tag="div">
                        {formatMoneyWithFallback(app?.cost?.forecast, currency)}
                      </Text>
                    </div>
                  </FlexContainer>
                </Col>
              </Row>

              <SpendsChart
                width={chartWidth}
                height={200}
                spends={app?.cost?.currentMonthDaily || []}
                events={[]}
              />
            </Box>
          </Col>
        ))}
      </Row>
    </LoaderWrap>
  );
}
